/**
 * AdminPmsQueuePage — File d'attente PMS pour le cabinet.
 *
 * Vue admin sur ce que les clients envoient depuis leur portail :
 *  Section « Immeubles & propriétaires » : enquêtes de crédit, RL-31, baux, renouvellements, paiements Stripe,
 *    revenus à approuver, documents & dépenses, T777, kilométrages immeuble, suivi des travaux.
 *  Section « Entreprise » (Batch #14.314) : factures de revenus · dépenses & reçus · kilométrage reçus de la tuile
 *    « Entreprise » du portail — on consulte, on saisit en Tenue de livres, puis « Marquer traité ».
 *
 * Architecture : tuiles compteurs par section en haut, panneau actif détaillé en bas ; `?tab=` dans l'URL.
 * Batch #14.154 — Refactor : panneaux extraits dans `pages/admin/pms/*`.
 */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Shield, Receipt, FileSignature, Banknote, RefreshCw,
  CheckSquare, Home, Car, FolderArchive, TrendingUp, Wrench, Building2, Briefcase,
} from "lucide-react";
import { toast } from "@/lib/toast";
import T777Submissions from "@/pages/admin/T777Submissions";

import { API, authH } from "./pms/_shared";
import { ENT_TABS, ENT_TAB_KIND } from "./pms/_entreprise";
import CreditChecksPanel from "./pms/CreditChecksPanel";
import Rl31RequestsPanel from "./pms/Rl31RequestsPanel";
import LeasesPanel from "./pms/LeasesPanel";
import PaymentsPanel from "./pms/PaymentsPanel";
import PendingRevenuesPanel from "./pms/PendingRevenuesPanel";
import DocsAndExpensesPanel from "./pms/DocsAndExpensesPanel";
import MileagePanel from "./pms/MileagePanel";
import RentIncreasePanel from "./pms/RentIncreasePanel";
import WorkOrdersPanel from "./pms/WorkOrdersPanel";  // Batch #14.191
import EntreprisePanel from "./pms/EntreprisePanel";

const TABS = [
  { id: "credit",         label: "Enquêtes de crédit",  icon: Shield,        tone: "cyan",    countKey: "credit_checks_pending" },
  { id: "rl31",           label: "Demandes RL-31",       icon: Receipt,       tone: "amber",   countKey: "rl31_requests_pending" },
  { id: "leases",         label: "Baux récents (30j)",   icon: FileSignature, tone: "blue",    countKey: "leases_recent_30d" },
  { id: "rent_increase",  label: "Avis de renouvellement", icon: TrendingUp,  tone: "rose",    countKey: "rent_increase_pending" },
  { id: "payments",       label: "Paiements Stripe",     icon: Banknote,      tone: "violet",  countKey: "stripe_pad_pending" },
  { id: "revenues",       label: "Revenus en attente",   icon: CheckSquare,   tone: "emerald", countKey: "manual_revenues_pending" },
  { id: "docs_expenses",  label: "Documents & Dépenses", icon: FolderArchive, tone: "rose",    countKey: "docs_expenses_combined" },
  { id: "t777",           label: "Bureau à domicile et dépenses d'emploi (client)", icon: Home, tone: "indigo", countKey: "t777_submissions_count" },
  { id: "mileage",        label: "Kilométrages immeuble", icon: Car,          tone: "amber",   countKey: "mileage_count" },
  // Batch #14.191 — Suivi des travaux (work orders)
  { id: "work_orders",    label: "Suivi des travaux",   icon: Wrench,        tone: "amber",   countKey: "work_orders_open" },
];
const ALL_TAB_IDS = new Set([...TABS, ...ENT_TABS].map((t) => t.id));

const TONES = {
  cyan:    { bg: "bg-cyan-50",    border: "border-cyan-200",    iconBg: "bg-cyan-100 text-cyan-700",       text: "text-cyan-700",    active: "ring-cyan-400" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   iconBg: "bg-amber-100 text-amber-700",     text: "text-amber-700",   active: "ring-amber-400" },
  blue:    { bg: "bg-blue-50",    border: "border-blue-200",    iconBg: "bg-blue-100 text-blue-700",       text: "text-blue-700",    active: "ring-blue-400" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  iconBg: "bg-violet-100 text-violet-700",   text: "text-violet-700",  active: "ring-violet-400" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", iconBg: "bg-emerald-100 text-emerald-700", text: "text-emerald-700", active: "ring-emerald-400" },
  rose:    { bg: "bg-rose-50",    border: "border-rose-200",    iconBg: "bg-rose-100 text-rose-700",       text: "text-rose-700",    active: "ring-rose-400" },
  indigo:  { bg: "bg-indigo-50",  border: "border-indigo-200",  iconBg: "bg-indigo-100 text-indigo-700",   text: "text-indigo-700",  active: "ring-indigo-400" },
};

function TileGrid({ tabs, summary, loading, activeTab, onSelect, cols }) {
  return (
    <div className={`grid grid-cols-2 lg:grid-cols-3 ${cols} gap-3`}>
      {tabs.map((tab) => {
        const count = summary?.[tab.countKey] ?? 0;
        const tone = TONES[tab.tone];
        const isActive = activeTab === tab.id;
        return (
          <button key={tab.id} onClick={() => onSelect(tab.id)}
            className={`${tone.bg} ${tone.border} border-2 rounded-xl p-4 text-left transition-all ${isActive ? `ring-2 ${tone.active} ring-offset-2 scale-[1.01]` : "hover:scale-[1.01] hover:shadow-sm"}`}
            data-testid={`admin-pms-tile-${tab.id}`}>
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-xl ${tone.iconBg} flex items-center justify-center`}><tab.icon className="w-5 h-5" /></div>
              <span className={`text-3xl font-bold ${tone.text}`} data-testid={`admin-pms-count-${tab.id}`}>{loading ? "…" : count}</span>
            </div>
            <div className="mt-3 text-xs font-semibold text-slate-700">{tab.label}</div>
            <div className="mt-1 text-[10px] text-slate-500">Cliquer pour voir →</div>
          </button>
        );
      })}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, hint, testId }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-5 first:mt-0" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-500" />
      <h2 className="text-sm font-bold text-slate-700">{title}</h2>
      <span className="text-[11px] text-slate-500">{hint}</span>
    </div>
  );
}

export default function AdminPmsQueuePage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const activeTab = ALL_TAB_IDS.has(sp.get("tab")) ? sp.get("tab") : "credit";
  const selectTab = (id) => { const n = new URLSearchParams(sp); if (id === "credit") n.delete("tab"); else n.set("tab", id); setSp(n, { replace: true }); };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pms/queue`, { headers: authH() });
      setSummary(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const entKind = ENT_TAB_KIND[activeTab];
  const grid = { summary, loading, activeTab, onSelect: selectTab };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-pms-queue-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Boîte de réception PMS</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Tâches et pièces reçues des clients depuis leur portail — ouvrir, accepter/refuser, marquer traité ou archiver. La gestion des travaux se fait dans la fiche Immeuble ; la saisie comptable dans Tenue de livres.
          </p>
        </div>
        <button onClick={fetchSummary} disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 disabled:opacity-40" data-testid="admin-pms-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      <SectionTitle icon={Building2} title="Immeubles & propriétaires" hint="demandes reçues des propriétaires d'immeubles" testId="admin-pms-section-immeubles" />
      <TileGrid tabs={TABS} cols="xl:grid-cols-5" {...grid} />

      <SectionTitle icon={Briefcase} title="Entreprise" hint="factures, reçus et trajets envoyés depuis la tuile « Entreprise » du portail — à saisir en Tenue de livres puis marquer traité" testId="admin-pms-section-entreprise" />
      <TileGrid tabs={ENT_TABS} cols="xl:grid-cols-3" {...grid} />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-5">
        {activeTab === "credit"        && <CreditChecksPanel onUpdate={fetchSummary} />}
        {activeTab === "rl31"          && <Rl31RequestsPanel onUpdate={fetchSummary} />}
        {activeTab === "leases"        && <LeasesPanel />}
        {activeTab === "rent_increase" && <RentIncreasePanel onUpdate={fetchSummary} />}
        {activeTab === "payments"      && <PaymentsPanel />}
        {activeTab === "revenues"      && <PendingRevenuesPanel onUpdate={fetchSummary} />}
        {activeTab === "docs_expenses" && <DocsAndExpensesPanel onUpdate={fetchSummary} />}
        {activeTab === "t777"          && <T777Submissions />}
        {activeTab === "mileage"       && <MileagePanel />}
        {activeTab === "work_orders"   && <WorkOrdersPanel />}
        {entKind && <EntreprisePanel kind={entKind} onUpdate={fetchSummary} />}
      </div>
    </div>
  );
}
